import "@/styles/marquee.css";

import { Image } from "@/4-components/primitives/Image";
import type { ImageMeta } from "@/5-shared/types";
import { cn } from "@/lib/utils";

// Auto-discover every logo in the partners folder at build time, with its intrinsic
// size (`?meta`), so each <img> reserves its box before the file arrives.
const partnerImages = import.meta.glob<{ default: ImageMeta }>(
  "../assets/partners/*.{png,jpg,jpeg,svg,webp}",
  { eager: true, query: "?meta" },
);

// Derive a readable name from the filename for alt text.
const partners = Object.entries(partnerImages).map(([path, module]) => {
  const filename = path.split("/").pop()?.split(".")[0] ?? "";
  const name = filename
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return { logo: module.default, name };
});

/**
 * Auto-scrolling, hover-to-pause marquee of partner/sponsor logos. `bg` is the band's
 * background (white by default); on any other colour the logos multiply into a track
 * painted that colour, so a JPEG logo's white box vanishes (the track needs the paint:
 * its stacking context, the moving transform, isolates the blend from what is behind it).
 */
export default function Partners({ bg }: { bg?: `bg-${string}` }) {
  const logoClass = cn(
    "max-h-8 md:max-h-12 w-auto object-contain",
    bg && "mix-blend-multiply",
  );
  return (
    <div
      className={cn(
        "shell overflow-hidden py-4 relative marquee-fade",
        bg ?? "bg-white",
      )}
    >
      <div className={cn("flex animate-marquee gap-8 md:gap-16", bg)}>
        {partners.map((partner, index) => (
          <div
            key={`first-${index}`}
            className="flex-shrink-0 transition-all duration-300"
          >
            <Image
              src={partner.logo}
              alt={partner.name}
              className={logoClass}
            />
          </div>
        ))}
        {/* Duplicate track (hidden from AT) so the -50% loop is seamless. */}
        {partners.map((partner, index) => (
          <div
            key={`second-${index}`}
            className="flex-shrink-0 transition-all duration-300"
            aria-hidden="true"
          >
            <Image
              src={partner.logo}
              alt={partner.name}
              className={logoClass}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
